import { Layer } from "../constants";
import { JumpIn } from "../definitions/jumpIn";
import { Numeric } from "./math";
import { FloorNames, type Terrain } from "./terrain";
import { Vec, type Vector } from "./vector";

/**
 * The rules of jumping in (1.06, see definitions/jumpIn.ts), shared by the server, which flies the planes and the chutes,
 * and the page, which draws them and the ring where you'll land. Pure: no game, no clock of its own.
 */

/** a plane's way over the map: it reaches `start` at its clock's 0 (`planeClock`) and `end` at `routeMs` */
export interface JumpRoute {
    readonly start: Vector
    readonly end: Vector
}

/**
 * the planes' two ways over a map `width` by `height`: the first along the diagonal from the bottom left to the top
 * right (the owner's red line), the second from the top left to the bottom right (his purple one), each `inset` in from
 * the corners, over the sea; `flips` turns a way round (it's rolled every match: either end can be where a plane starts)
 */
export function jumpRoutes(width: number, height: number, flips: readonly [boolean, boolean]): [JumpRoute, JumpRoute] {
    const inset = JumpIn.inset;
    const red: JumpRoute = { start: Vec(inset, height - inset), end: Vec(width - inset, inset) };
    const purple: JumpRoute = { start: Vec(inset, inset), end: Vec(width - inset, height - inset) };
    const turned = (route: JumpRoute, flip: boolean): JumpRoute => flip ? { start: route.end, end: route.start } : route;
    return [turned(red, flips[0]), turned(purple, flips[1])];
}

export const routeLength = (route: JumpRoute): number => Vec.len(Vec.sub(route.end, route.start));

/** how long a plane takes over its way */
export const routeMs = (route: JumpRoute): number => routeLength(route) / JumpIn.planeSpeed;

/** the way a plane flies (radians) */
export const routeHeading = (route: JumpRoute): number => Math.atan2(route.end.y - route.start.y, route.end.x - route.start.x);

/**
 * a plane's own clock, `elapsed` ms after the first player joined: negative while it's still coming in, 0 as it reaches
 * the start of its way (the first plane `boardMs` after the first player joined, the second `staggerMs` after it)
 */
export const planeClock = (plane: number, elapsed: number): number => elapsed - JumpIn.boardMs - plane * JumpIn.staggerMs;

/** where a plane is at its clock `t`: before 0 coming in from off the map, after the end of its way flying on */
export function planeAt(route: JumpRoute, t: number): Vector {
    const length = routeLength(route);
    if (length === 0) return Vec.clone(route.start);
    return Vec.lerp(route.start, route.end, (JumpIn.planeSpeed * t) / length);
}

/** where a player aboard a plane is at its clock `t`: the plane's place, kept to its way (never off the map) */
export const aboardAt = (route: JumpRoute, t: number): Vector => planeAt(route, Numeric.clamp(t, 0, routeMs(route)));

/** a player aboard may jump: the plane is over its way, and they've been aboard a moment (`aboardFor` ms) */
export const mayJump = (t: number, aboardFor: number): boolean => t >= 0 && aboardFor >= JumpIn.boardHoldMs;

/** the plane is at the end of its way: whoever is still aboard jumps there */
export const mustJump = (route: JumpRoute, t: number): boolean => t >= routeMs(route);

/** a player joining may still board a plane: enough of its way is left */
export const mayBoard = (route: JumpRoute, t: number): boolean => t <= routeMs(route) - JumpIn.lateBoardMs;

/**
 * the plane a player joining boards: of those they may still board (`boardable`), the one given fewer players so far
 * (`given`), either on a tie, at random (each plane carrying half; nobody can tell from the order the players joined in
 * who is in which: 1.06's audit); undefined if they may board none
 */
export function choosePlane(given: readonly number[], boardable: readonly boolean[], random: () => number = Math.random): number | undefined {
    let fewest = Infinity;
    for (let plane = 0; plane < given.length; plane++) if (boardable[plane]) fewest = Math.min(fewest, given[plane]);
    const ties: number[] = [];
    for (let plane = 0; plane < given.length; plane++) if (boardable[plane] && given[plane] === fewest) ties.push(plane);
    if (ties.length === 0) return undefined;
    return ties[Math.min(ties.length - 1, Math.floor(random() * ties.length))];
}

/** how high (m) a player is `t` ms after jumping */
export const fallHeight = (t: number): number => JumpIn.height * Numeric.clamp(1 - t / JumpIn.fallMs, 0, 1);

/** how long (ms) a player `height` m up has to the ground (braking, 1.09, `1 / JumpIn.brakeRate` as long) */
export const fallLeft = (height: number, braking = false): number =>
    (JumpIn.fallMs * Numeric.clamp(height / JumpIn.height, 0, 1)) / (braking ? JumpIn.brakeRate : 1);

/** the keys (or a phone's stick) that steer a chute */
export interface SteerInput {
    readonly up: boolean
    readonly down: boolean
    readonly left: boolean
    readonly right: boolean
    /** a phone's stick is held (`angle` its way) */
    readonly moving?: boolean
    readonly angle?: number
}

/** the way a chute is steered: a phone's stick its own way, the keys one of eight (none held, none) */
export function steerDirection(input: SteerInput): Vector {
    if (input.moving === true && input.angle !== undefined && Number.isFinite(input.angle)) return Vec.fromPolar(input.angle);
    // (a key is pressed or it isn't: never NaN, whatever a packet held)
    const key = (pressed: boolean | undefined): number => (pressed === true ? 1 : 0);
    let x = key(input.right) - key(input.left);
    let y = key(input.down) - key(input.up);
    if (x !== 0 && y !== 0) {
        x *= Math.SQRT1_2;
        y *= Math.SQRT1_2;
    }
    return Vec(x, y);
}

/** a chute's drift (units a ms) after `dt` ms steered `direction`: it turns toward its new heading over `steerMs` */
export function steer(velocity: Vector, direction: Vector, dt: number): Vector {
    const k = 1 - Math.exp(-Math.max(0, dt) / JumpIn.steerMs);
    return Vec(
        velocity.x + (direction.x * JumpIn.glideSpeed - velocity.x) * k,
        velocity.y + (direction.y * JumpIn.glideSpeed - velocity.y) * k
    );
}

/**
 * where a chute at `position`, drifting `velocity`, comes down if it's steered `direction` for the `left` ms to the
 * ground (the HUD's ring): its drift turns toward the steered one as `steer` turns it, so the ground it covers is the
 * steered drift's, less what it takes to get there
 */
export function landingGuess(position: Vector, velocity: Vector, direction: Vector, left: number): Vector {
    const time = Math.max(0, left);
    const gx = direction.x * JumpIn.glideSpeed, gy = direction.y * JumpIn.glideSpeed;
    const lag = JumpIn.steerMs * (1 - Math.exp(-time / JumpIn.steerMs));
    return Vec(position.x + gx * time + (velocity.x - gx) * lag, position.y + gy * time + (velocity.y - gy) * lag);
}

/** how far a chute gets across the ground from where its player jumped, steered one way all the way down */
export const chuteReach = (): number => landingGuess(Vec(0, 0), Vec(0, 0), Vec(1, 0), JumpIn.fallMs).x;

/**
 * whether a player of `radius` at `point` would stand in the water or at its very edge, where the map is drawn wet (the
 * page rounds the shore's corners: 1.06.1's lab saw a chute come down at the sea's edge look to stand in it): the point
 * itself, and on the sand (the shore's and the rivers' banks: the water is only ever next to sand) eight points round it
 * `radius` + 2 units off. Where a chute may come down (the server's `JumpInManager.isOpen`, the page's ring)
 */
export function wetGround(terrain: Pick<Terrain, "getFloor">, point: Vector, radius: number): boolean {
    const floor = terrain.getFloor(point, Layer.Ground);
    if (isWater(floor)) return true;
    if (floor !== FloorNames.Sand) return false;
    const out = radius + 2;
    for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        if (isWater(terrain.getFloor(Vec(point.x + Math.cos(angle) * out, point.y + Math.sin(angle) * out), Layer.Ground))) return true;
    }
    return false;
}

/** the water's floor, frozen or not (0.98.5: in the snow the rivers and the sea are ice; no chute comes down on them either) */
export const isWater = (floor: FloorNames): boolean => floor === FloorNames.Water || floor === FloorNames.Ice;

/**
 * The open ground nearest `start` for a player of `radius` on a map `width` by `height` (`open` says where they could
 * stand: on the server, by every solid; on the page, by what it knows of the map): `start` itself (kept inside the map),
 * or in rings round it, nearest first, `JumpIn.landingStep` apart (twice that past `JumpIn.landingNear`), out to
 * `JumpIn.landingSearch`; undefined if there's none that near. A ring's open points come in runs round it, each where
 * the ring crosses into open ground: of each run its middle point (along a wall, the one straight out from `start`, the
 * wall's nearest point; the run's ends move a long way along the wall for a hair's move of `start`), and of those the
 * nearest `toward` when it's given (a chute's: where its drift takes it, the least glide), else the nearest `start`.
 * Where a chute aimed at a roof or the water comes down (the server's `JumpInManager`), and where the page's ring says
 * it will (1.06.1's second review: of a ring's points, all as near `start`, the one taken was the rounding's, and the
 * page's ring, worked out a little earlier, and the server's chute took points on one wall 12 units apart)
 */
export function nearestOpen(start: Vector, radius: number, width: number, height: number, open: (point: Vector) => boolean, toward?: Vector): Vector | undefined {
    const margin = radius + 1;
    const inside = (point: Vector): Vector => Vec(
        Numeric.clamp(point.x, margin, width - margin),
        Numeric.clamp(point.y, margin, height - margin)
    );
    const from = inside(start);
    if (open(from)) return from;
    for (let ring = JumpIn.landingStep; ring <= JumpIn.landingSearch; ring += ring < JumpIn.landingNear ? JumpIn.landingStep : 2 * JumpIn.landingStep) {
        // (a point every 6 units round the ring, 16 at least; each ring's turned a little from the last's)
        const count = Math.max(16, Math.ceil((2 * Math.PI * ring) / 6));
        const turn = ring * 0.37;
        const points: Vector[] = [];
        const opens: boolean[] = [];
        for (let i = 0; i < count; i++) {
            const angle = turn + (i / count) * Math.PI * 2;
            const point = inside(Vec(from.x + Math.cos(angle) * ring, from.y + Math.sin(angle) * ring));
            points.push(point);
            opens.push(open(point));
        }
        if (!opens.includes(true)) continue;
        const closed = opens.indexOf(false);
        const goal = toward ?? start;
        let best: Vector | undefined;
        let bestDistance = Infinity;
        const consider = (point: Vector): void => {
            const distance = Vec.squaredLen(Vec.sub(point, goal));
            if (distance < bestDistance) {
                best = point;
                bestDistance = distance;
            }
        };
        if (closed === -1) {
            // (open all round: the nearest the goal)
            for (const point of points) consider(point);
        } else {
            // each run of open points round the ring (from the first closed one on, so none is cut in two): its middle
            for (let k = 1; k <= count; k++) {
                const first = (closed + k) % count;
                if (!opens[first] || opens[(first + count - 1) % count]) continue;
                let length = 0;
                while (length < count && opens[(first + length) % count]) length++;
                consider(points[(first + Math.floor((length - 1) / 2)) % count]);
            }
        }
        if (best) return best;
    }
    return undefined;
}

/**
 * The final approach: where a chute at `position`, drifting `velocity`, can still come down in the `left` ms to the
 * ground, gliding at up to `JumpIn.settleSpeed` (`settleDirection`): round where its drift alone takes it, as far as
 * that glide goes in the time left once it's turned (nine tenths of it, to be sure of getting there)
 */
export function settleReach(position: Vector, velocity: Vector, left: number): { readonly center: Vector, readonly radius: number } {
    const time = Math.max(0, left);
    const lag = JumpIn.steerMs * (1 - Math.exp(-time / JumpIn.steerMs));
    return {
        center: Vec(position.x + velocity.x * lag, position.y + velocity.y * lag),
        radius: 0.9 * JumpIn.settleSpeed * Math.max(0, time - lag)
    };
}

/** whether `point` is in a chute's reach (`settleReach`) */
export const inReach = (reach: { readonly center: Vector, readonly radius: number }, point: Vector): boolean =>
    Vec.squaredLen(Vec.sub(point, reach.center)) <= reach.radius * reach.radius;

/**
 * The final approach (the last `JumpIn.settleMs` of the way down): the way a chute at `position`, drifting `velocity`, is
 * steered to come down on `spot` in the `left` ms to the ground, as `steer` takes it (1 is a full glide; up to
 * `JumpIn.settleSpeed`): the steady drift that `landingGuess` says lands there, worked out again every tick
 */
export function settleDirection(position: Vector, velocity: Vector, spot: Vector, left: number): Vector {
    const time = Math.max(0, left);
    const lag = JumpIn.steerMs * (1 - Math.exp(-time / JumpIn.steerMs));
    // (landingGuess(position, velocity, u / glideSpeed, time) is `spot` for u (time - lag) = spot - position - velocity
    // lag; near the ground, time - lag is next to nothing, and the glide's top speed holds what's asked of it)
    const ahead = Math.max(time - lag, 10);
    let x = (spot.x - position.x - velocity.x * lag) / ahead;
    let y = (spot.y - position.y - velocity.y * lag) / ahead;
    const speed = Math.hypot(x, y);
    if (!Number.isFinite(speed)) return Vec(0, 0);
    if (speed > JumpIn.settleSpeed) {
        x *= JumpIn.settleSpeed / speed;
        y *= JumpIn.settleSpeed / speed;
    }
    return Vec(x / JumpIn.glideSpeed, y / JumpIn.glideSpeed);
}

/** a height in a byte (thirds of a metre): the players' updates carry it */
export const heightToByte = (height: number): number => Math.round(Numeric.clamp(height, 0, JumpIn.height) * 3);
export const heightFromByte = (byte: number): number => byte / 3;
